<template>
  <div class="internal-chat">
    <div class="internal-chat__list-pane">
      <conversation-list
        :conversations="conversations"
        :active-conversation-id="activeConversationId"
        :is-loading="uiFlags.isFetchingConversations"
        @select="selectConversation"
        @new-chat="showNewChatModal = true"
      />
    </div>
    <div class="internal-chat__message-pane">
      <message-panel
        v-if="activeConversation"
        :key="activeConversation.id"
        :conversation="activeConversation"
        :messages="activeMessages"
        :current-user-id="currentUserId"
        :is-loading="uiFlags.isFetchingMessages"
      />
      <div v-else class="internal-chat__empty-state">
        <fluent-icon icon="chat-multiple" size="48" />
        <p>{{ $t('INTERNAL_CHAT.EMPTY_STATE') }}</p>
      </div>
    </div>

    <new-chat-modal
      :show="showNewChatModal"
      @close="showNewChatModal = false"
      @start="startChatWith"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ConversationList from './ConversationList.vue';
import MessagePanel from './MessagePanel.vue';
import NewChatModal from './NewChatModal.vue';

export default {
  components: {
    ConversationList,
    MessagePanel,
    NewChatModal,
  },
  props: {
    conversationId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      showNewChatModal: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUserId: 'getCurrentUserID',
      conversations: 'internalChat/getConversations',
      activeConversationId: 'internalChat/getActiveConversationId',
      uiFlags: 'internalChat/getUIFlags',
    }),
    activeConversation() {
      return this.$store.getters['internalChat/getConversationById'](
        this.activeConversationId
      );
    },
    activeMessages() {
      if (!this.activeConversationId) {
        return [];
      }
      return this.$store.getters['internalChat/getMessagesByConversationId'](
        this.activeConversationId
      );
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.openConversation(Number(newId));
        }
      },
    },
  },
  mounted() {
    this.$store.dispatch('internalChat/fetchConversations');
  },
  beforeDestroy() {
    this.$store.dispatch('internalChat/clearActiveConversationId');
  },
  methods: {
    selectConversation(conversation) {
      if (Number(this.activeConversationId) !== Number(conversation.id)) {
        this.$router.push({
          name: 'internal_chat_conversation',
          params: { conversationId: conversation.id },
        });
      }
      this.openConversation(conversation.id);
    },
    async openConversation(conversationId) {
      this.$store.dispatch(
        'internalChat/setActiveConversationId',
        conversationId
      );
      await this.$store.dispatch('internalChat/fetchMessages', {
        conversationId,
      });
      this.$store.dispatch(
        'internalChat/markConversationDelivered',
        conversationId
      );
      this.$store.dispatch('internalChat/markConversationRead', conversationId);
    },
    async startChatWith(userId) {
      const conversation = await this.$store.dispatch(
        'internalChat/startConversation',
        userId
      );
      this.showNewChatModal = false;
      this.selectConversation(conversation);
    },
  },
};
</script>

<style scoped lang="scss">
.internal-chat {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.internal-chat__list-pane {
  width: 32rem;
  flex-shrink: 0;
  height: 100%;
}

.internal-chat__message-pane {
  flex: 1;
  min-width: 0;
  height: 100%;
}

.internal-chat__empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--s-500);
  gap: var(--space-small);
}
</style>
